/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import { ReactSortable } from 'react-sortablejs'
import { generate as generateUniqueId } from 'randomstring'
import isEqual from 'lodash/isEqual'
import FormHelperText from '@mui/material/FormHelperText'
import TableButtons from 'components/TableButtons'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { CELL_TYPES, Value } from '../Cell'
import { Row } from '../Row'
import { Head } from '../Head'
import { HeadValue } from '../HeadCell'
import * as classes from './styles'

export type TempRow = {
  id: string
  values: Value[]
  isEditable: boolean
  isEmpty: boolean
}

export const getEmptyCell = (columnId: string): Value => ({
  type: CELL_TYPES.TEXT,
  text: '',
  columnId,
})

const getValues = (values: Value[], columns: HeadValue[]) =>
  columns.map(({ columnId }) => values.find((value) => value.columnId === columnId) ?? getEmptyCell(columnId))

const getTempRows = (rows: Value[][], columns: HeadValue[]): TempRow[] =>
  rows.map((initialValues) => ({
    id: generateUniqueId(),
    values: getValues(initialValues, columns),
    isEditable: false,
    isEmpty: false,
  }))

const getEmptyRow = (columns: HeadValue[]): TempRow => ({
  id: generateUniqueId(),
  values: columns.map(({ columnId }) => getEmptyCell(columnId)),
  isEditable: true,
  isEmpty: true,
})

export type TableProps = {
  id?: string
  initialColumns: HeadValue[]
  initialRows: Value[][]
  onChange: (cols: HeadValue[], rows: Value[][]) => void
  initialIsColumnsEditable?: boolean
  error?: string
  errorRows?: Record<number, boolean>
  setReset?: (cb: (cols: HeadValue[], rows: Value[][]) => void) => void
  isSortable?: boolean
  noItemsText?: string
  maxColumnsCount?: number
  maxRowsCount?: number
}

export const Table = ({
  id,
  initialColumns,
  initialRows,
  onChange,
  initialIsColumnsEditable = false,
  error,
  errorRows,
  setReset,
  isSortable,
  noItemsText = 'No rows added',
  maxColumnsCount,
  maxRowsCount,
}: TableProps) => {
  const [tempColumns, setTempColumns] = useState<HeadValue[]>(initialColumns)
  const [isColumnsEditable, setIsColumnsEditable] = useState(initialIsColumnsEditable)
  const [tempRows, setTempRows] = useState<TempRow[]>(getTempRows(initialRows, initialColumns))
  const [rowIndexToRemove, setRowIndexToRemove] = useState<number | null>(null)

  useEffect(() => {
    setTempColumns(initialColumns)
    setTempRows(getTempRows(initialRows, initialColumns))
    setIsColumnsEditable(initialIsColumnsEditable)
  }, [id])

  useEffect(() => {
    setReset?.((cols, rows) => {
      setTempColumns(cols)
      setTempRows(getTempRows(rows, cols))
    })
  }, [setReset])

  useEffect(() => {
    onChange(
      tempColumns,
      tempRows.map(({ values }) => values)
    )
  }, [tempColumns, tempRows])

  const handleChangeValues = useCallback(
    (id: string) => (values: Value[]) => {
      setTempRows((prevRows) => {
        const rows = [...prevRows]
        const index = rows.findIndex((row) => row.id === id)
        rows[index] = {
          ...rows[index],
          isEmpty: false,
          values,
        }
        return rows
      })
    },
    []
  )

  const handleChangeIsEditable = useCallback(
    (id: string) => (isEditable: boolean) => {
      setTempRows((prevRows) => {
        const rows = [...prevRows]
        const index = rows.findIndex((row) => row.id === id)
        rows[index] = {
          ...rows[index],
          isEditable,
        }
        return rows
      })
    },
    []
  )

  const handleRemoveRow = useCallback(
    (id: string) => () => {
      const index = tempRows.findIndex((row) => row.id === id)

      if (index !== -1) {
        setRowIndexToRemove(index)
      }
    },
    [tempRows]
  )

  const handleConfirmRemoveRow = useCallback(() => {
    if (rowIndexToRemove !== null) {
      setTempRows((prevRows) => {
        const rows = [...prevRows]
        rows.splice(rowIndexToRemove, 1)
        return rows
      })
      setRowIndexToRemove(null)
    }
  }, [rowIndexToRemove])

  const handleAddRow = useCallback(() => {
    setTempRows((prevRows) => [...prevRows, getEmptyRow(tempColumns)])
  }, [tempColumns])

  const handleSort = useCallback((sortedRowsNew: Array<{ id: string }>) => {
    setTempRows((prevRows) => {
      const newRows = sortedRowsNew.map(({ id }) => prevRows.find((row) => row.id === id) as TempRow)

      if (isEqual(prevRows, newRows)) {
        return prevRows
      }

      return newRows
    })
  }, [])

  const handleChangeColumns = (columns: HeadValue[]) => {
    setTempColumns(columns)
    setTempRows((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        values: getValues(row.values, columns),
      }))
    )
  }

  const renderRows = () => {
    if (!tempRows.length) {
      return noItemsText && <div css={classes.emptyText}>{noItemsText}</div>
    }

    return tempRows.map((row, index) => (
      <Row
        key={row.id}
        values={row.values}
        onChange={handleChangeValues(row.id)}
        onRemove={handleRemoveRow(row.id)}
        isColumnsEditable={isColumnsEditable}
        isEditable={row.isEditable}
        onIsEditableChange={handleChangeIsEditable(row.id)}
        isError={errorRows?.[index]}
        isEmpty={row.isEmpty}
        isSortable={!!isSortable}
      />
    ))
  }

  return (
    <div data-testid={`table-${id}`}>
      <div css={classes.scrollable}>
        <div css={classes.content}>
          <Head
            columns={tempColumns}
            onChange={handleChangeColumns}
            isEditable={isColumnsEditable}
            onIsEditableChange={setIsColumnsEditable}
            isSortable={isSortable}
            maxColumnsCount={maxColumnsCount}
          />
          {isSortable ? (
            <ReactSortable list={tempRows} setList={handleSort} css={classes.wrapper}>
              {renderRows()}
            </ReactSortable>
          ) : (
            <div css={classes.wrapper}>{renderRows()}</div>
          )}
        </div>
      </div>
      <TableButtons
        itemName="Row"
        onAddItem={handleAddRow}
        disabled={maxRowsCount !== undefined && tempRows.length >= maxRowsCount}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
      <ConfirmationModal
        open={rowIndexToRemove !== null}
        title={`Deleting row #${(rowIndexToRemove ?? 0) + 1}`}
        subtitle={`Are you sure you want to delete this row?`}
        onClose={() => setRowIndexToRemove(null)}
        onConfirm={handleConfirmRemoveRow}
        confirmText="Delete"
        closeText="Cancel"
      />
    </div>
  )
}
